import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  OrderPayment,
  PaginatedOrderPaymentResponse,
} from 'src/models/OrderPayment.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

export const useGetOrderPayments = () => {
  const [payments, setPayments] = useState<OrderPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchPayments = async (
    orderId: string,
    page = 1,
    limit = 15,
    sortBy = 'paymentDate',
    order = 'desc'
  ) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        offset: page,
        limit: limit,
        sortBy: sortBy,
        order: order,
      };

      const response =
        await axiosTokenInstance.get<PaginatedOrderPaymentResponse>(
          `${apiUrls.GET_ORDER_PAYMENTS}/${orderId}/payments`,
          {
            params,
            withCredentials: true,
          }
        );

      const mappedPayments = (response.data.data || []).map(
        (payment: OrderPayment) => ({
          ...payment,
          orderId: payment.orderId,
          amount: payment.paidAmount,
          receivedBy: payment.receivedBy,
          transactionReference: payment.transactionReference,
        })
      );

      setPayments(mappedPayments);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (err.response?.status === 404) {
        console.error(err.response.data?.message);
        setPayments([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deletePayment = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        `${apiUrls.DELETE_ORDER_PAYMENT}/${id}`,
        {
          withCredentials: true,
        }
      );

      setPayments((prevPayments) =>
        prevPayments.filter((payment) => payment.id !== id)
      );

      if (payments.length === 1) {
        setPayments([]);
      }
      showSnackbar(response.data.message, 'success');
    } catch (error: unknown) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  const fetchPaymentById = async (paymentId: string) => {
    try {
      const response = await axiosTokenInstance.get<{ data: OrderPayment }>(
        `${apiUrls.GET_ORDER_PAYMENT_BY_ID}/${paymentId}`,
        {
          withCredentials: true,
        }
      );
      return response.data.data;
    } catch (error: unknown) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      throw error;
    }
  };

  return {
    payments,
    loading,
    totalItems,
    totalPages,
    currentPage,
    fetchPayments,
    deletePayment,
    fetchPaymentById,
  };
};
